<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { computed, reactive, ref } from 'vue';
import { string, object } from 'yup';

const { tenant, facebookAdsAudience, facebookAdsAudienceTypes } = defineProps({
    tenant: {
        required: true,
        type: Object,
    },
    facebookAdsAudience: {
        required: false,
        type: Object,
    },
    facebookAdsAudienceTypes: {
        required: true,
        type: Array,
    },
});

const { route } = useRoute();

const form = reactive({
    type: facebookAdsAudience
        ? facebookAdsAudienceTypes.find(
              (type) => type.value === facebookAdsAudience.type.value,
          )
        : facebookAdsAudienceTypes[0],
    data: facebookAdsAudience ? facebookAdsAudience.data : {},
    name: facebookAdsAudience ? facebookAdsAudience.name : '',
});

const isSubmitting = ref(false);

const validationSchema = computed(() => {
    let schema = {
        type: object().required('Ce champ est obligatoire.'),
        name: string().required('Ce champ est obligatoire.'),
    };

    if (
        form.type.value === 'customers_who_have_bought_n_time' ||
        form.type.value === 'customers_who_have_never_bought_last_n_days' ||
        form.type.value === 'customers_who_have_bought_more_than_n_time' ||
        form.type.value === 'customers_who_have_unprocessed_cart_last_n_days' ||
        form.type.value === 'customers_who_have_bought_last_n_days' ||
        form.type.value ===
            'customers_who_have_not_bought_last_n_days_but_bought_before' ||
        form.type.value ===
            'customers_who_have_not_bought_last_n_days_but_bought_last_t_days'
    ) {
        schema = {
            ...schema,
            data: object().shape({
                n: string().required('Ce champ est obligatoire.'),
            }),
        };
    }

    if (
        form.type.value ===
            'customers_who_have_bought_more_than_n_time_last_t_days' ||
        form.type.value ===
            'customers_who_have_not_bought_last_n_days_but_bought_last_t_days'
    ) {
        schema = {
            ...schema,
            data: object().shape({
                n: string().required('Ce champ est obligatoire.'),
                t: string().required('Ce champ est obligatoire.'),
            }),
        };
    }

    return object(schema);
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.sma.postAudience', {
                tenant: tenant.id,
                ...(facebookAdsAudience
                    ? { facebookAdsAudienceId: facebookAdsAudience.id }
                    : {}),
            }),
            {
                type: form.type.value,
                data: form.data,
                name: form.name,
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route(
                    'front.projectManagement.sma.audiences',
                    tenant ? { tenant: tenant.id } : {},
                ),
            );
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <FormRoot
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <FormColumn>
            <FormContent>
                <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2">
                    <InputField
                        v-model="form.name"
                        label="Nom"
                        field-name="name"
                        field-id="name"
                        type="text"
                        required
                    />
                </div>

                <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2">
                    <ListboxField
                        v-model="form.type"
                        field-id="type"
                        field-name="type"
                        label="Type"
                        :options="facebookAdsAudienceTypes"
                        required
                        :disabled="facebookAdsAudience"
                    >
                        <template #buttonContent>
                            {{ form.type.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>
                </div>

                <div
                    class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4"
                >
                    <InputField
                        v-if="
                            form.type.value ===
                                'customers_who_have_bought_n_time' ||
                            form.type.value ===
                                'customers_who_have_unprocessed_cart_last_n_days' ||
                            form.type.value ===
                                'customers_who_have_never_bought_last_n_days' ||
                            form.type.value ===
                                'customers_who_have_bought_more_than_n_time' ||
                            form.type.value ===
                                'customers_who_have_bought_more_than_n_time_last_t_days' ||
                            form.type.value ===
                                'customers_who_have_bought_last_n_days' ||
                            form.type.value ===
                                'customers_who_have_not_bought_last_n_days_but_bought_before' ||
                            form.type.value ===
                                'customers_who_have_not_bought_last_n_days_but_bought_last_t_days'
                        "
                        v-model="form.data.n"
                        label="N"
                        field-name="data.n"
                        field-id="data.n"
                        type="number"
                        step="1"
                        min="1"
                        required
                    />

                    <InputField
                        v-if="
                            form.type.value ===
                                'customers_who_have_bought_more_than_n_time_last_t_days' ||
                            form.type.value ===
                                'customers_who_have_not_bought_last_n_days_but_bought_last_t_days'
                        "
                        v-model="form.data.t"
                        label="T"
                        field-name="data.t"
                        field-id="data.t"
                        type="number"
                        step="1"
                        min="1"
                        required
                    />
                </div>
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="facebookAdsAudience"
                    :url="
                        route('front.projectManagement.sma.deleteAudience', {
                            tenant: tenant.id,
                            facebookAdsAudienceId: facebookAdsAudience.id,
                        })
                    "
                    :callback="
                        () => {
                            router.visit(
                                route('front.projectManagement.sma.audiences', {
                                    tenant: tenant.id,
                                }),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
